<template>
  <div class="validate-address-container">
    <div v-if="errorMessage" class="error">
      {{ errorMessage }}
    </div>
    <button
      type="button"
      class="btn btn-secondary"
      :class="{ disabled: submitting }"
      @click.prevent="validateAddress()"
    >
      Validate Address
    </button>
    <inline-loading-indicator v-if="submitting"></inline-loading-indicator>
    <input v-model="validationType" type="hidden" name="validation_type" />
    <input type="hidden" name="validated_address" :value="validatedAddress" />

    <!-- this is in the DOM, but we're pulling it out into the SWAL. This way we get the value of Vue while using the Swal.html stuff -->
    <div class="hidden">
      <div ref="validatedAddresses">
        <ul>
          <li v-for="(validAddress, index) in validatedAddresses" :key="index">
            <button
              class="btn"
              :class="{
                'btn-primary': index === 0,
                'btn-secondary': index !== 0,
              }"
              @click.prevent="pickAddress(validAddress)"
            >
              {{ validAddress.address }} {{ validAddress.address2 }}
              <br />
              {{ validAddress.city }}, {{ validAddress.state }}
              {{ validAddress.county }}
              {{ validAddress.zip }}
              {{ validAddress.country }}
            </button>
          </li>
          <li v-if="validatedAddresses && validatedAddresses.length === 0">
            No matching address could be found
          </li>
        </ul>
        <a href="#" @click.prevent="selfCertifyAddress()">
          Keep my original address. I certify it is correct.
        </a>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  filters: {
    stringify(value) {
      return JSON.stringify(value);
    },
  },

  props: {
    addressSelector: {
      type: String,
      required: true,
    },
    address2Selector: {
      type: String,
      required: true,
    },
    citySelector: {
      type: String,
      required: true,
    },
    countySelector: {
      type: String,
      required: true,
    },
    zipSelector: {
      type: String,
      required: true,
    },
    stateSelector: {
      type: String,
      required: true,
    },
    countrySelector: {
      type: String,
      required: true,
    },
    saveSelector: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      address: {
        address: null,
        address2: null,
        city: null,
        county: null,
        state: null,
        zip: null,
        country: null,
      },
      validatedAddresses: [],
      validated: false,
      validationType: null,
      errorMessage: null,
      submitting: false,
    };
  },

  computed: {
    validatedAddress() {
      if (this.validationType === "smarty-streets") {
        return JSON.stringify(this.address);
      }
      return "";
    },
  },

  watch: {
    validated(val) {
      document.querySelector(this.saveSelector).disabled = !val;
    },
  },

  mounted() {
    document.querySelector(this.saveSelector).disabled = true;
  },

  methods: {
    initializeAddressDataFromExternalForm() {
      Object.keys(this.address).forEach((key) => {
        this.address[key] = document.querySelector(
          this[`${key}Selector`]
        ).value;
      });
    },

    updateAddressWithValidatedAddress(validAddress) {
      Object.keys(this.address).forEach((key) => {
        this.address[key] =
          typeof validAddress[key] !== "undefined" ? validAddress[key] : "";
      });
    },

    updateExternalFormWithValidatedAddress() {
      Object.keys(this.address).forEach((key) => {
        document.querySelector(this[`${key}Selector`]).value =
          this.address[key];
      });
    },

    validateAddress() {
      this.validatedAddresses = [];
      this.errorMessage = null;

      this.initializeAddressDataFromExternalForm();

      if (!this.checkRequiredFields()) {
        this.errorMessage = "Please fill out the address before validating.";
        return;
      }

      this.submitting = true;

      this.$http
        .post("/address/validate-accuracy", this.address)
        .then((response) => {
          this.validatedAddresses = response.data;

          this.$swal.fire({
            title: "Select a validated address",
            html: this.$refs.validatedAddresses,
            showCancelButton: true,
            showConfirmButton: false,
            width: "45rem",
          });
        })
        .catch((e) => {
          if (e.response.status !== 422) {
            this.errorMessage =
              "We are temporarily unable to validate your address. You may proceed without validation.";
          } else {
            const errors = Object.values(e.response.data.errors)
              .flat()
              .join(" ");
            this.errorMessage = `Unable to validate address: ${errors}`;
          }
        })
        .finally(() => {
          this.submitting = false;
        });
    },

    checkRequiredFields() {
      return (
        ["address", "city", "state", "zip", "country"].filter(
          (field) => !this.address[field]
        ).length === 0
      );
    },

    pickAddress(validAddress) {
      this.validated = true;

      this.updateAddressWithValidatedAddress(validAddress);
      this.updateExternalFormWithValidatedAddress();

      this.validationType = "smarty-streets";
      this.$swal.close();
    },

    selfCertifyAddress() {
      this.validated = true;

      this.validationType = "self-certified";
      this.$swal.close();
    },
  },
};
</script>

<style scoped>
.validate-address-container {
  position: relative;
  text-align: center;
}

button {
  white-space: normal;
}

ul {
  list-style: none;
  padding-inline-start: 0px;
  margin-top: 2rem;
}

li {
  padding-bottom: 1rem;
}
</style>
